import tw from 'twin.macro'
import logo from 'assets/images/TrybeLogo.png'
import { Link, useLocation, useMatch } from 'react-router-dom'
import { FaUser } from 'react-icons/fa'
import { some } from 'lodash'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { useListenUserProfileQuery } from 'modules/Users/userApi'
import { useListenStripeProductIdQuery, useListenStripePubKeyQuery } from 'modules/ProductInfo/productInfoApi'
import { useAuth } from 'modules/Auth/hooks/useAuth'
import { useListenCommunityReportsQuery } from 'modules/Community/communityApi'
import { formatBadgeCount } from 'modules/Community/communityUtils'
import { useCanSeeContentStats } from 'modules/ContentStats/useCanSeeContentStats'
import { useCanSeeReports } from 'modules/Reports/useCanSeeReports'

//YOUR APP
const LAYOUT = '/layout'
export const NEW_USER_QUIZ = '/quiz' //Exported for quick linking when coach tries to delete content used in quiz
const FEATURED_RESOURCES = '/featured'
const COMMUNITY = '/community'
const COMMUNITY_MODERATION = '/community/moderation'

//COLLECTIONS
const COLLECTIONS = '/collections'

//PROGRAMS
const PROGRAMS = '/programs'
const PROGRAM_GROUPS = '/series'

//EXERCISES
const EXERCISES = '/exercises'

//VIDEOS
const VIDEOS = '/videos'

//SELL
export const SELL = '/sell'
export const STRIPE_SETUP = '/sell/setup'
export const PRICING = '/sell/pricing'
export const COUPONS = '/sell/coupons'
export const ONE_TIME_PRODUCT = '/sell/product'
export const PAGEBUILDER_SALES_PAGE = '/sell/salesPage'
export const PAGEBUILDER_CHECKOUT_PAGE = '/sell/checkoutPage'

//REPORTS (shown to coaches as Attribution)
export const REPORTS = '/attribution'
// The page's first address. Old bookmarks and shared links redirect to REPORTS.
export const LEGACY_REPORTS = '/reports'
//CONTENT STATS (same audience as Attribution)
export const CONTENT_STATS = '/content-stats'

//CHILD PATHS
const PROGRESSIONS = '/progressions'
// const UPLOAD = '/upload'
const RESOURCES = '/resources'

//Child-to-Parent path mapping
// Library = everything the coach makes; Your app = app configuration.
// Community is its own top-level menu (desktop only — on a phone coaches use
// the mobile app itself; the mobile dashboard exists for video uploads).
const librarySubRoutes = [PROGRAMS, PROGRAM_GROUPS, EXERCISES, PROGRESSIONS, VIDEOS, COLLECTIONS]
const yourAppSubRoutes = [LAYOUT, NEW_USER_QUIZ, FEATURED_RESOURCES, RESOURCES]
const sellSubRoutes = [SELL, STRIPE_SETUP, PRICING, COUPONS, PAGEBUILDER_SALES_PAGE, PAGEBUILDER_CHECKOUT_PAGE]
const communitySubRoutes = [COMMUNITY, COMMUNITY_MODERATION]
const analyticsSubRoutes = [CONTENT_STATS, REPORTS]

const greenUnderline = `underline underline-offset-8 decoration-2 decoration-tGreen`
const noHeaderOutline =
  'outline-none ring-0 ring-offset-0 hover:outline-none hover:ring-0 active:outline-none active:ring-0 focus:outline-none focus:ring-0 focus:ring-offset-0 focus-visible:outline-none focus-visible:ring-0'
const activeRoute = `text-sm md:text-lg font-semibold text-tGray-dark underline ${noHeaderOutline} ${greenUnderline}`
const inactiveRoute = `text-sm md:text-lg font-semibold text-tGray-med hover:text-tGreen ${noHeaderOutline}`
const activeSubroute = `flex text-sm md:text-base items-center px-3 py-2 md:py-3 font-medium relative focus:cursor-pointer ${noHeaderOutline} ${greenUnderline}`
const inactiveSubroute = `flex text-sm md:text-base items-center px-3 py-2 md:py-3 font-medium relative focus:cursor-pointer focus:bg-gray-100 ${noHeaderOutline}`
const dropdownContent =
  'w-auto md:w-80 bg-white rounded-lg shadow-md border border-gray-100 mt-2 py-3 md:py-4 px-1 z-[999]'
const hiddenOnMobileClasses = 'hidden md:block'

function Header(props) {
  const location = useLocation() //Used to check if user is in "Home" page, which is the logo and therefore all routes should be colored as active
  const isProgramView = useMatch(`${PROGRAMS}/:programId`)
  const { userId } = useAuth()
  const { data: profile } = useListenUserProfileQuery({ userId })
  const coachOrgId = profile?.coachOrgId || ''
  const { data: stripePubKey } = useListenStripePubKeyQuery({ coachOrgId })
  const stripePubKeyLoading = stripePubKey === undefined || stripePubKey?.isLoading
  const stripePubKeyExists = !stripePubKeyLoading && Boolean(stripePubKey)
  const { data: stripeProductId } = useListenStripeProductIdQuery({ coachOrgId })
  const stripeProductIdLoading = stripeProductId === undefined || stripeProductId?.isLoading
  const stripeProductIdExists = !stripeProductIdLoading && Boolean(stripeProductId)
  const showReports = useCanSeeReports()
  const { canSee: canSeeContentStats } = useCanSeeContentStats()

  //Add paths that should not have a header here
  if (location.pathname === '/signup') {
    return null
  }

  return (
    <div
      css={[
        tw`flex h-[60px] md:h-[80px] py-4 md:py-10 bg-white items-center sticky top-0 z-[100] shadow-sm`,
        isProgramView && tw`absolute inset-x-0`,
      ]}
    >
      <Link to='/' className={`flex flex-row items-center ml-4 mr-2 md:mr-5 md:ml-5 ${noHeaderOutline}`}>
        <img src={logo} height='40px' width='40px' alt='Trybe logo' />
      </Link>
      <div className='flex space-x-4'>
        <YourAppPaths location={location} />
        <LibraryPaths location={location} />
        <CommunityPaths location={location} coachOrgId={coachOrgId} />
        {!stripePubKeyLoading && (
          <SellPaths
            location={location}
            stripePubKeyExists={stripePubKeyExists}
            stripeProductIdExists={stripeProductIdExists}
          />
        )}
        {showReports && <AnalyticsPaths location={location} showContentStats={canSeeContentStats} />}
      </div>
      <TopRightLinks />
    </div>
  )
}

function LibraryPaths({ location }) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger className={noHeaderOutline}>
        <div className={isActiveParent(location, librarySubRoutes) ? activeRoute : inactiveRoute}>Library</div>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content className={dropdownContent} align='start'>
          <div className='space-y-0 md:space-y-1'>
            {createDropdownLink(EXERCISES, 'Exercises', location)}
            {createDropdownLink(VIDEOS, 'Full Length Videos', location)}
            {createDropdownLink(PROGRAMS, 'Programs', location)}
            {createDropdownLink(PROGRESSIONS, 'Progressions', location)}
            {createDropdownLink(COLLECTIONS, 'Collections', location)}
            {createDropdownLink(PROGRAM_GROUPS, 'Series', location)}
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}

function CommunityPaths({ location, coachOrgId }) {
  // The Moderation item carries the number of open reports, so a coach sees
  // from any page that something is waiting. The moderation page reuses the
  // same query (RTK dedupes identical subscriptions).
  const { data: reportsData } = useListenCommunityReportsQuery({ coachOrgId }, { skip: !coachOrgId })
  const openReportCount =
    reportsData === undefined || reportsData?.isLoading
      ? 0
      : Object.values(reportsData || {}).filter((r) => r?.status === 'open').length
  const moderationText = `Moderation${openReportCount > 0 ? ` · ${formatBadgeCount(openReportCount)}` : ''}`

  const isModerationRouteActive = isActiveChild(location, COMMUNITY_MODERATION)
  const isFeedRouteActive = !isModerationRouteActive && isActiveChild(location, COMMUNITY)

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger className={`${noHeaderOutline} ${hiddenOnMobileClasses}`}>
        <div className={isActiveParent(location, communitySubRoutes) ? activeRoute : inactiveRoute}>Community</div>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content className={dropdownContent} align='start'>
          <div className='space-y-0 md:space-y-1'>
            {createDropdownLink(COMMUNITY, 'Feed', location, false, isFeedRouteActive)}
            {createDropdownLink(COMMUNITY_MODERATION, moderationText, location, false, isModerationRouteActive)}
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}

function YourAppPaths({ location }) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger className={`${noHeaderOutline} ${hiddenOnMobileClasses}`}>
        <div className={isActiveParent(location, yourAppSubRoutes) ? activeRoute : inactiveRoute}>Your app</div>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content className={dropdownContent} align='start'>
          {createDropdownLink(LAYOUT, 'App Layout', location)}
          {createDropdownLink(NEW_USER_QUIZ, 'New User Quiz', location)}
          {createDropdownLink(FEATURED_RESOURCES, 'Featured Resources', location)}
          {createDropdownLink(RESOURCES, 'Additional Resources', location)}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}

function SellPaths({ location, stripePubKeyExists, stripeProductIdExists }) {
  const isCouponsRouteActive = location.pathname === COUPONS
  const isStripeSetupRouteActive = location.pathname === STRIPE_SETUP
  const isProductsRouteActive = isCouponsRouteActive || isStripeSetupRouteActive ? false : isActiveChild(location, SELL)

  // Until the org has connected Stripe and created its product, the only Sell page
  // that exists is Setup, so the link goes straight there instead of a dropdown.
  if (!stripePubKeyExists || !stripeProductIdExists) {
    return (
      <div className={`${noHeaderOutline} ${hiddenOnMobileClasses}`}>
        <div className={isActiveParent(location, sellSubRoutes) ? activeRoute : inactiveRoute}>
          <Link to={STRIPE_SETUP} className={noHeaderOutline}>
            Sell
          </Link>
        </div>
      </div>
    )
  }

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger className={`${noHeaderOutline} ${hiddenOnMobileClasses}`}>
        <div className={isActiveParent(location, sellSubRoutes) ? activeRoute : inactiveRoute}>Sell</div>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content className={dropdownContent} align='start'>
          <div className='space-y-0 md:space-y-1'>
            {createDropdownLink(SELL, 'Products', location, false, isProductsRouteActive)}
            {createDropdownLink(COUPONS, 'Coupons', location, false, isCouponsRouteActive)}
            {createDropdownLink(STRIPE_SETUP, 'Settings', location, false, isStripeSetupRouteActive)}
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}

function AnalyticsPaths({ location, showContentStats }) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger className={`${noHeaderOutline} ${hiddenOnMobileClasses}`}>
        <div className={isActiveParent(location, analyticsSubRoutes) ? activeRoute : inactiveRoute}>Analytics</div>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content className={dropdownContent} align='start'>
          <div className='space-y-0 md:space-y-1'>
            {showContentStats && createDropdownLink(CONTENT_STATS, 'Content Stats', location)}
            {createDropdownLink(REPORTS, 'Attribution', location)}
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}

function TopRightLinks(props) {
  return (
    <div className='flex flex-row ml-auto mr-4 md:mr-5 items-center space-x-2'>
      {/* <Link to='/help'>
        <FiHelpCircle className='w-6 h-6 text-tGray-med' />
      </Link> */}
      <Link to='/account' className={noHeaderOutline}>
        <FaUser className='w-6 h-6 text-tGray-med' />
      </Link>
    </div>
  )
}

function createDropdownLink(path, text, location, hiddenOnMobile = false, isActive) {
  const active = typeof isActive === 'boolean' ? isActive : isActiveChild(location, path)
  return (
    <DropdownMenu.Item asChild>
      <Link
        to={path}
        className={`${active ? activeSubroute : inactiveSubroute} ${hiddenOnMobile ? hiddenOnMobileClasses : ''}`}
      >
        {text}
      </Link>
    </DropdownMenu.Item>
  )
}

function isActiveParent(location, subRoutes) {
  if (!location?.pathname || !subRoutes?.length) return false
  return some(subRoutes, (route) => location.pathname.startsWith(route))
}

function isActiveChild(location, path) {
  if (!location?.pathname || !path) return false

  return location.pathname.startsWith(path)
}

export default Header
